import { FieldErrors, FieldValues } from 'react-hook-form';
import { HiOutlineBriefcase } from 'react-icons/hi';
import { LuMail, LuBuilding, LuGlobe, LuSettings } from 'react-icons/lu';
import {
  AutomatedEmailCampaignType,
  IAutomatedCampaignConfig,
  IAutomatedEmailCampaignSetting,
  IAutomatedEmailCampaignSettingListPayload,
} from 'wisestep-api';
import { FieldOption, UserRoundIcon, WsSelectObj } from 'wisestep-base';

import { IOption } from '../../../components/SettingsIndex';
import { campaignConfigType } from '../schemas/campaignSettingsSchema';
export const campaignHeaderHelper = {
  campaignStatus: {
    header: 'Default Campaign',
    subHeader:
      'Activate the default campaign to automatically email top candidates when a new job is posted.',
  },
  totalEmails: {
    header: 'Total Emails',
    subHeader: 'Specify the number of candidates who will receive this email notification.',
  },
  aiReplyEnabled: {
    header: 'AI Reply Assistant',
    subHeader: 'AI-assisted replies for responding to candidate emails.',
  },
  manualReply: {
    header: 'Manual Reply',
    subHeader: 'Manually assign a user to reply to candidate emails.',
  },
  callBackRequest: {
    header: 'Callback Request',
    subHeader: 'Invite a user to handle callback requests from candidates.',
  },
  clientContactId: {
    header: 'Choose a Client SPOC *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Client SPOC.',
  },
  clientId: {
    header: 'Choose a Client *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Client.',
  },
  jobId: {
    header: 'Choose a Job *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Job.',
  },
  source: {
    header: 'Choose a source  *',
    subHeader:
      'Activate the campaign to automatically email top candidates when a new job is posted for respective Source.',
  },
};

export const campaignTabs = [
  {
    tabName: 'Email Campaign',
  },
  {
    tabName: 'Email Template',
  },
];

export const switchDialogHelper = {
  aiReplyEnabled: {
    header: 'Switching to AI Reply Assistant',
    subHeader: 'Campaigns for new job postings will be replied with AI-assistant.',
    showIcon: true,
    alteredField: 'manualReply',
  },
  manualReply: {
    header: 'Switching to Manual reply',
    subHeader: 'Campaigns for new job postings will require a manual response.',
    showIcon: true,
    alteredField: 'aiReplyEnabled',
  },
  callBackRequest: {
    header: 'Callback Request',
    subHeader: 'Invite a user to handle callback requests from candidates.',
    showIcon: false,
  },
};

export const automatedNavigationOptions: IOption[] = [
  {
    label: 'Default',
    icon: <LuMail />,
    url: '/automated-email-campaign-settings/',
  },
  {
    label: 'Client',
    icon: <LuBuilding />,
    url: '/automated-email-campaign-settings/client',
  },
  {
    label: 'Client SPOC',
    icon: <UserRoundIcon />,
    url: '/automated-email-campaign-settings/client-spoc',
  },
  {
    label: 'Job',
    icon: <HiOutlineBriefcase />,
    url: '/automated-email-campaign-settings/job',
  },
  {
    label: 'Source',
    icon: <LuGlobe />,
    url: '/automated-email-campaign-settings/source',
  },
  {
    label: 'Campaign Settings',
    icon: <LuSettings />,
    url: '/automated-email-campaign-settings/campaign-settings',
  },
];

export const defaultAutomatedEmailCampaignSettingListPayload = (
  type: AutomatedEmailCampaignType
): IAutomatedEmailCampaignSettingListPayload => {
  return {
    page: 0,
    size: 20,
    type: type,
    search: '',
  };
};

export const routePath: Record<AutomatedEmailCampaignType, string> = {
  [AutomatedEmailCampaignType.JOB]: '/automated-email-campaign-settings/job',
  [AutomatedEmailCampaignType.SOURCE]: '/automated-email-campaign-settings/source',
  [AutomatedEmailCampaignType.CLIENT]: '/automated-email-campaign-settings/client',
  [AutomatedEmailCampaignType.DEFAULT]: '/automated-email-campaign-settings/',
  [AutomatedEmailCampaignType.CLIENT_SPOC]: '/automated-email-campaign-settings/client-spoc',
};

export const createCampaignEntity = (data: FieldValues, type: AutomatedEmailCampaignType) => {
  const entity = {
    id: data.id,
    totalEmails: data.totalEmails,
    aiReplyEnabled: data.aiReplyEnabled,
    manualReply: data.manualReply,
    manualReplyFrom: data?.manualEmail?.email,
    callBackRequest: data.callBackRequest,
    callBackRequestFrom: data?.callbackEmail?.email,
    emailBody: data.emailBody,
    emailReplyPrompts: data.emailReplyPrompts,
    subject: data.subject,
    campaignStatus: data.campaignStatus,
    emailCheck: false,
    fromEmail: data.fromEmail,
    preferredAccountId: data.preferredAccountId,
  } as IAutomatedEmailCampaignSetting;

  switch (type) {
    case AutomatedEmailCampaignType.CLIENT:
      if (data.client) {
        entity.metaDetails = data.client;
        entity.clientId = data.client.value;
      }
      break;

    case AutomatedEmailCampaignType.CLIENT_SPOC:
      if (data.clientContact) {
        entity.metaDetails = data.clientContact;
        entity.clientContactId = data.clientContact.id;
      }
      break;

    case AutomatedEmailCampaignType.JOB:
      if (data.job) {
        entity.metaDetails = data.job;
        entity.jobId = data.job.id;
      }
      break;

    case AutomatedEmailCampaignType.SOURCE:
      if (data.source) {
        entity.source = data.source;
      }
      break;
  }
  return entity;
};

export const extractAllMessages = (errors: FieldErrors): string[] => {
  const messages: string[] = [];

  const traverse = (obj: FieldErrors | any) => {
    for (const key in obj) {
      const value = obj[key];
      if (!value) continue;
      if (typeof value.message === 'string') {
        messages.push(value.message);
      }
      if (typeof value === 'object') {
        traverse(value);
      }
    }
  };

  traverse(errors);
  return messages;
};

export const getCampaignUniqueId = (
  data: FieldValues,
  campaignType: AutomatedEmailCampaignType
) => {
  let id;
  switch (campaignType) {
    case AutomatedEmailCampaignType.CLIENT:
      id = data.client.value;
      break;

    case AutomatedEmailCampaignType.CLIENT_SPOC:
      id = data.clientContact.id;
      break;
    case AutomatedEmailCampaignType.JOB:
      id = data.job.id;
      break;

    case AutomatedEmailCampaignType.SOURCE:
      id = data.source;
      break;
  }
  return id;
};

export const getCampaignSettingsColumns = (
  campaignType: AutomatedEmailCampaignType
): FieldOption[] => {
  const baseFields: FieldOption[] = [
    { id: 'createdOn', checked: true },
    { id: 'status', checked: true },
    { id: 'id', checked: true },
  ];

  let prefixFields: FieldOption[] = [];

  switch (campaignType) {
    case AutomatedEmailCampaignType.CLIENT:
      prefixFields = [{ id: 'client', checked: true, required: true }];
      break;
    case AutomatedEmailCampaignType.CLIENT_SPOC:
      prefixFields = [
        { id: 'clientSpocName', checked: true, required: true },
        { id: 'client', checked: true, required: true },
      ];
      break;
    case AutomatedEmailCampaignType.JOB:
      prefixFields = [
        { id: 'title', checked: true, required: true },
        { id: 'jobCode', checked: true, required: true },
      ];
      break;
    case AutomatedEmailCampaignType.SOURCE:
      prefixFields = [{ id: 'source', checked: true, required: true }];
      break;
  }

  return [...prefixFields, ...baseFields];
};

export const buildCampaignConfigDefaultValues = (
  campaignConfigData: IAutomatedCampaignConfig
): campaignConfigType => {
  const campaignConfigSetting = {
    id: campaignConfigData?.id ?? '',
    //Candidate selection Rules default Values
    threshold: [campaignConfigData?.threshold ?? 90, 100],
    recentCampaign: campaignConfigData?.recentCampaign ?? 0,
    recentCampaignDuration: campaignConfigData?.recentCampaignDuration ?? 'Days',
    recentCheck: campaignConfigData?.recentCheck ?? false,
    nonResponsiveCandidateCheck: false,
    nonResponsiveCandidateStatus: 'Open',
    excludeCandidateCheck: false,

    // Additional Rules default Values
    excludeCandidateEmailCheck: false,
    excludeCandidateEmail: 0,
    excludeCandidateEmailDuration: 'Days',
    deliverabilityCheck: false,
    excludeCandidatehiringCheck: false,
    lastSubmission: campaignConfigData?.lastSubmission ?? 0,
    submissionDuration: campaignConfigData?.submissionDuration ?? 'Days',
    submissionCheck: campaignConfigData?.submissionCheck ?? false,
    businessDefinitions: campaignConfigData?.businessDefinitions ?? ([] as string[]),
    validEmailCheck: false,

    //Sender and Campaign Controls

    senderEmail: {
      fromEmail: campaignConfigData?.fromEmail ?? '',
      label: campaignConfigData?.fromEmail ?? '',
    },
    // fromEmail: campaignConfigData?.fromEmail ?? '',
    // preferredAccountId: campaignConfigData?.preferredAccountId ?? '',
    // fromName: campaignConfigData?.fromName ?? '',
    // signature: campaignConfigData?.signature ?? '',
    followUpEmailCheck: false,
    followUpEmail: 0,
    followUpEmailDuration: 'Days',
  } as campaignConfigType;

  return campaignConfigSetting;
};

// export const buildAutomatedEmailCampaignConfigurationSettings = (
//   data: campaignConfigType,
//   cardType: CampaignSettingTypes,
//   prevStateSavedValues: campaignConfigType
// ): IAutomatedCampaignConfig => {
//   switch (cardType) {
//     case CampaignSettingTypes.CANDIDATE_RULES: {
//       const entity = {
//         ...prevStateSavedValues,
//         threshold: data?.threshold?.[0] ?? 90,
//         recentCheck: data?.recentCheck,
//         recentCampaignDuration: data?.recentCampaignDuration,
//         recentCampaign: data?.recentCampaign ?? 0,
//         nonResponsiveCandidateCheck: data?.nonResponsiveCandidateCheck,
//         nonResponsiveCandidateStatus: data?.nonResponsiveCandidateStatus,
//         excludeCandidateCheck: data?.excludeCandidateCheck,
//       } as unknown as IAutomatedCampaignConfig;
//       return entity;
//     }
//     case CampaignSettingTypes.ADDITIONALE_RULES:
//       return {
//         ...prevStateSavedValues,
//         excludeCandidateEmailCheck: data?.excludeCandidateEmailCheck,
//         excludeCandidateEmail: data?.excludeCandidateEmail,
//         excludeCandidateEmailDuration: data?.excludeCandidateEmailDuration,
//         deliverabilityCheck: data?.deliverabilityCheck,
//         excludeCandidatehiringCheck: data?.excludeCandidatehiringCheck,
//         lastSubmission: data?.lastSubmission ?? 0,
//         submissionDuration: data?.submissionDuration ?? 'Days',
//         submissionCheck: data?.submissionCheck ?? false,
//         businessDefinitions: data?.businessDefinitions ?? ([] as string[]),
//         validEmailCheck: false,
//       } as IAutomatedCampaignConfig;

//     case CampaignSettingTypes.SENDER_EMAIL_RULES:
//       return {
//         ...prevStateSavedValues,
//         fromEmail: data?.senderEmail?.fromEmail ?? '',
//         fromName: data?.senderEmail?.fromName ?? '',
//         preferredAccountId: data?.senderEmail?.id ?? '',
//         signature: data?.senderEmail?.signatureText ?? '',
//         followUpEmailCheck: data?.followUpEmailCheck ?? false,
//         followUpEmail: data?.followUpEmail ?? prevStateSavedValues.followUpEmail,
//         followUpEmailDuration:
//           data?.followUpEmailDuration ?? prevStateSavedValues.followUpEmailDuration,
//       } as IAutomatedCampaignConfig;
//   }
// };
// const campaignSettingErrorMap: Record<CampaignSettingTypes, string[]> = {
//   [CampaignSettingTypes.CANDIDATE_RULES]: ['recentCheck'],
//   [CampaignSettingTypes.ADDITIONALE_RULES]: ['excludeCandidateEmailCheck', 'submissionCheck'],
//   [CampaignSettingTypes.SENDER_EMAIL_RULES]: ['followUpEmailCheck'],
// };

// export const checkCampaignSettingsValidations = (data, cardType: CampaignSettingTypes) => {
//   campaignSettingErrorMap[cardType].map((val) => data?.val);
// };

export const timeSpanOptions: WsSelectObj[] = [
  { label: 'Days', value: 'Days' },
  { label: 'Months', value: 'Months' },
  { label: 'Weeks', value: 'Weeks' },
];

export const candidateStatusOptions = [
  { label: 'Open', value: 'Open' },
  { label: 'Reply', value: 'Reply' },
];

export const relavanceSliderOptions = [
  { value: 30, label: '30%' },
  { value: 40, label: '40%' },
  { value: 50, label: '50%' },
  { value: 60, label: '60%' },
  { value: 70, label: '70%' },
  { value: 80, label: '80%' },
  { value: 90, label: '90%' },
  { value: 100, label: '100%' },
];

export enum CampaignSettingTypes {
  CANDIDATE_RULES,
  ADDITIONALE_RULES,
  SENDER_EMAIL_RULES,
}
